import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect, useState } from 'react'
import { FlexColumn, FlexRow } from '../../atoms/layout/Flex'
import { ConstructionType } from '../../../types/job'
import { JobSelectors } from '../../../store/job/selectors'
import SelectString, { SelectItem } from '../../atoms/inputs/SelectString'
import SelectControl from '../../atoms/inputs/SelectControl'
import Button from '../../atoms/inputs/Button'
import { PlusIcon, RemoveIcon } from '../../atoms/display/Icons'
import TextField from '../../atoms/inputs/TextField'
import Form from '../../atoms/inputs/Form'
import IconButton from '../../atoms/inputs/IconButton'
import { CompanyActions } from '../../../store/company'
import { CompanySelectors } from '../../../store/company/selectors'

type Params = {
  constructions: {
    typeId: string
    detail?: string
  }[]
}

function makeConstructionItems(constructionTypes: ConstructionType[]) {
  return constructionTypes.map((type) => ({ value: type.id, label: type.name }))
}

type Props = {
  index: number
  error?: string
}

function ConstructionTypeSelect({ index, error }: Props) {
  const industryTypes = useSelector(JobSelectors.industryTypes)
  const form = useFormContext<Params>()
  const [industryTypeId, setIndustryTypeId] = useState<string>()
  const industryItems = industryTypes.map((type) => ({ value: type.id, label: type.name }))
  const [constructionItems, setConstructionItems] = useState<SelectItem[]>([])

  useEffect(() => {
    const constructionTypeId = form.getValues(`constructions.${index}.typeId`)
    const industryType = industryTypes.find((type) =>
      type.constructionTypes.map((constructionType) => constructionType.id).includes(constructionTypeId ?? '')
    )
    if (industryType) {
      setIndustryTypeId(industryType.id)
      const constructionTypes = industryType.constructionTypes ?? []
      setConstructionItems(makeConstructionItems(constructionTypes))
    }
  }, [form, index, industryTypes])

  const handleSelectIndustry = useCallback(
    (typeId: string) => {
      setIndustryTypeId(typeId)
      const constructionTypes = industryTypes.find((type) => type.id === typeId)?.constructionTypes ?? []
      setConstructionItems(makeConstructionItems(constructionTypes))
    },
    [industryTypes]
  )

  return (
    <FlexRow align="flex-start">
      <SelectString value={industryTypeId} items={industryItems} onChange={handleSelectIndustry} size="sm" />
      <SelectControl
        items={constructionItems}
        size="sm"
        control={form.control}
        name={`constructions.${index}.typeId`}
        error={error}
      />
      <TextField register={form.register(`constructions.${index}.detail`)} size="md" placeholder="詳細" />
    </FlexRow>
  )
}

export default function CompanyIndustryTypes() {
  const company = useSelector(CompanySelectors.company)
  const dispatch = useDispatch()
  const form = useForm<Params>({
    defaultValues: {
      constructions: company?.constructions ?? [{}],
    },
  })
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'constructions' })

  const handleAdd = useCallback(() => {
    append({})
  }, [append])

  const handleRemove = useCallback(
    (index: number) => {
      remove(index)
    },
    [remove]
  )

  const handleSave = useCallback(
    (params: Params) => {
      dispatch(
        CompanyActions.saveConstructions({
          constructions: params.constructions
            .filter((construction) => construction.typeId != null)
            .map((construction) => ({ typeId: construction.typeId, detail: construction.detail })),
        })
      )
    },
    [dispatch]
  )

  return (
    <FormProvider {...form}>
      <Form onSubmit={form.handleSubmit(handleSave)}>
        <FlexColumn>
          {fields.map((field, index) => (
            <FlexRow key={field.id}>
              <ConstructionTypeSelect index={index} />
              <IconButton label="削除" onClick={() => handleRemove(index)}>
                <RemoveIcon />
              </IconButton>
            </FlexRow>
          ))}
          <Button icon={<PlusIcon />} onClick={handleAdd}>
            工種を追加する
          </Button>
          <Button type="submit">保存</Button>
        </FlexColumn>
      </Form>
    </FormProvider>
  )
}
